import Component from "@glimmer/component";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { bool, instanceOf } from "prop-types";
import { Task, task } from "ember-concurrency";
import user from "glesys-controlpanel/models/user";

import AlertMessage from "glesys-controlpanel/components/alert-message";
import Box from "glesys-controlpanel/components/box";
import IconsPassword from "glesys-controlpanel/components/icons/password";
import IconsChevronLeft from "glesys-controlpanel/components/icons/chevron-left";
import Icons2fa from "glesys-controlpanel/components/icons/2fa";
import GButton from "glesys-controlpanel/components/g-button";
import TaskForm from "glesys-controlpanel/components/task-form";
import Label from "glesys-controlpanel/components/label";
import GInput from "glesys-controlpanel/components/g-input";
import { t } from "ember-intl";
import { eq } from "ember-truth-helpers";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";

const BackButton = <template>
  <button
    class="mb-4 -mt-1 flex items-center hover:text-gray-500 hover:underline text-gray-600"
    type="submit"
    {{mergeClasses}}
    ...attributes
  >
    <IconsChevronLeft class="w-6 -ml-2" />
    <span>
      {{t "recover.confirm.back"}}
    </span>
  </button>
</template>;

@forbidExtraArgs
export default class RecoverConfirm2faUser extends Component {
  @arg(instanceOf(Task).isRequired) task;

  @arg(instanceOf(user).isRequired) user;

  @arg(bool) hasError = false;

  @tracked currentStep = "select-action";
  otp = null;
  password = null;

  @action
  setStep(step) {
    this.currentStep = step;
  }

  onOtpChange = ({ otp }) => (this.otp = otp);
  onPasswordChange = ({ password }) => (this.password = password);

  recoverTask = task(async () => {
    let { otp, password } = this;
    await this.task.perform({
      action: this.currentStep,
      otp,
      password,
    });
  });

  <template>
    <div ...attributes>
      {{#if (eq this.currentStep "select-action")}}
        <Box class="w-128">
          <:header>{{t "recover.title"}}</:header>
          <:default>
            {{#if @hasError}}
              <AlertMessage class="mb-6" data-test-general-error @type="error">
                {{t "recover.confirm.error"}}
              </AlertMessage>
            {{/if}}
            {{yield}}
            <div class="flex flex-col">
              <p class="text-base text-gray-500 pb-6">
                {{t "recover.confirm.2fa.is-external-enabled"}}
                <br />
                {{t "recover.confirm.2fa.reset-or-disable"}}
              </p>
              <div class="w-full flex flex-wrap pb-4">
                <Box
                  class="text-base w-full mb-4 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                  role="button"
                  data-test-2fa-reset-password
                  {{on "click" (fn this.setStep "2fa-reset-password")}}
                >
                  <div class="flex items-center">
                    <IconsPassword class="w-6 mr-3 -ml-1 text-gray-500" role="presentation" />
                    <span>{{t "recover.confirm.2fa.reset-password"}}</span>
                  </div>
                </Box>
                <Box
                  class="text-base w-full text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                  role="button"
                  data-test-revert-to-email-based-2fa
                  {{on "click" (fn this.setStep "revert-to-email-based")}}
                >
                  <div class="flex items-center">
                    <Icons2fa class="w-6 mr-3 -ml-1 text-gray-500" role="presentation" />
                    <span>{{t "recover.confirm.2fa.revert-to-email-based-2fa"}}</span>
                  </div>
                </Box>
              </div>
            </div>
          </:default>
        </Box>
      {{else}}
        {{#if (eq this.currentStep "2fa-reset-password")}}
          <Box class="w-128">
            <:header>{{t "recover.title"}}</:header>
            <:default>
              {{#if @hasError}}
                <AlertMessage class="mb-6" data-test-general-error @type="error">
                  {{t "recover.confirm.error"}}
                </AlertMessage>
              {{/if}}
              <BackButton {{on "click" (fn this.setStep "select-action")}} />
              <TaskForm
                class="w-full -mb-6"
                id="recover"
                data-test-recover-password-2fa
                @autocomplete="on"
                @method="post"
                @onChange={{this.onOtpChange}}
                @task={{this.recoverTask}}
              >
                <span class="text-base">{{t "recover.confirm.2fa.required"}}</span>
                <div class="my-4 text-gray-600">
                  <Label for="otp">
                    {{t "recover.confirm.2fa.label"}}
                  </Label>
                  <GInput
                    id="otp"
                    data-test-recover-one-time-password
                    name="otp"
                    value={{this.otp}}
                    required={{true}}
                  />
                </div>
              </TaskForm>
            </:default>
            <:footer>
              <GButton
                data-test-recover-button
                @type="submit"
                @color="green"
                @loading={{this.recoverTask.isRunning}}
                form="recover"
              >
                {{t "recover.confirm.create-new-password"}}
              </GButton>
            </:footer>
          </Box>
        {{else}}
          <Box class="w-128">
            <:header>{{t "recover.title"}}</:header>
            <:default>
              {{#if @hasError}}
                <AlertMessage class="mb-6" data-test-general-error @type="error">
                  {{t "recover.confirm.error"}}
                </AlertMessage>
              {{/if}}
              <BackButton {{on "click" (fn this.setStep "select-action")}} />
              <TaskForm
                class="w-full -mb-6"
                id="recover"
                data-test-recover-2fa
                @autocomplete="on"
                @method="post"
                @onChange={{this.onPasswordChange}}
                @task={{this.recoverTask}}
              >
                <span class="text-base">{{t "recover.confirm.2fa.lost"}}</span>
                <div class="my-4 text-gray-600">
                  <Label for="password">
                    {{t "recover.confirm.2fa.enter-password"}}
                  </Label>
                  <GInput
                    id="password"
                    data-test-recover-2fa-password
                    type="password"
                    name="password"
                    value={{this.password}}
                    required={{true}}
                  />
                </div>
              </TaskForm>
            </:default>
            <:footer>
              <GButton
                data-test-recover-button
                @type="submit"
                @color="green"
                @loading={{this.recoverTask.isRunning}}
                form="recover"
              >
                {{t "recover.confirm.2fa.revert-to-email-based-2fa"}}
              </GButton>
            </:footer>
          </Box>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
